import qs from 'qs';
import { createResource } from 'react-resource-router';

import {
  TestExecution,
  type TestExecutionResponse,
} from 'src/components/pipelines/models/TestReport';
import envBaseUrl from 'src/components/pipelines/utils/env-base-url';
import { SentryPipelinesTeam } from 'src/components/pipelines/utils/sentry';

import { PAGE_LEN } from '../components/test-management/utils';
import {
  COMMIT_QUERY_PARAM,
  EXECUTED_AFTER_QUERY_PARAM,
  EXECUTED_BEFORE_QUERY_PARAM,
  PAGE_QUERY_PARAM,
  PAGELEN_QUERY_PARAM,
  SORT_QUERY_PARAM,
  STATUS_QUERY_PARAM,
} from '../constants';

import { fetchData } from './utils';

export type Params = {
  testUuid: string;
  repositoryFullSlug: string;
};

export type Query = {
  page?: string;
  pagelen?: string;
  commit?: string;
  executedAfter?: string;
  executedBefore?: string;
  sort?: string;
  status?: string;
};

export function getTestExecutionsUrl(
  { repositoryFullSlug, testUuid }: Params,
  query: Query
) {
  const url = `${envBaseUrl(
    '/!api/internal'
  )}/repositories/${repositoryFullSlug}/tests/${encodeURIComponent(
    testUuid
  )}/executions`;

  const queryParams = qs.stringify(query || {}, {
    skipNulls: true,
    addQueryPrefix: true,
  });

  return `${url}${queryParams}`;
}

function buildTestExecutionsQuery(query: any): Query {
  return {
    [PAGE_QUERY_PARAM]: query.page || '1',
    [PAGELEN_QUERY_PARAM]: PAGE_LEN.toString(),
    [COMMIT_QUERY_PARAM]: query.commit,
    [EXECUTED_AFTER_QUERY_PARAM]: query.executedAfter,
    [EXECUTED_BEFORE_QUERY_PARAM]: query.executedBefore,
    [SORT_QUERY_PARAM]: query.sort || '-createdOn',
    [STATUS_QUERY_PARAM]: query.status,
  };
}

export const loadTestExecutionsResource = createResource({
  type: 'pipelinesTestExecutions',
  getKey: ({ match, query }) =>
    getTestExecutionsUrl(
      match.params as Params,
      buildTestExecutionsQuery(query)
    ),
  maxAge: 0,
  getData: async ({ match, query }) => {
    const testExecutionData = await fetchData(
      getTestExecutionsUrl(
        match.params as Params,
        buildTestExecutionsQuery(query)
      ),
      'test_executions_resource_exception',
      {},
      'json',
      SentryPipelinesTeam.Mangalyaan
    );

    return {
      page: testExecutionData.page,
      pagelen: testExecutionData.pagelen,
      size: testExecutionData.size,
      values: testExecutionData.values.map(
        (testExecution: TestExecutionResponse) =>
          new TestExecution(testExecution)
      ),
    };
  },
});
